import { useState, useRef, useCallback } from 'react';

const slides = [
  {
    id: 1,
    label: 'Cilt Bakımı',
    before: 'https://readdy.ai/api/search-image?query=before%20skin%20treatment%20close%20up%20face%20with%20uneven%20skin%20tone%20dull%20complexion%20large%20pores%20slight%20redness%20natural%20look%20no%20makeup%20neutral%20lighting%20beauty%20clinic%20photo%20realistic&width=600&height=450&seq=ba001&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=after%20professional%20facial%20skin%20care%20treatment%20glowing%20radiant%20healthy%20skin%20smooth%20even%20tone%20minimized%20pores%20clear%20complexion%20natural%20beauty%20clinic%20lighting&width=600&height=450&seq=ba002&orientation=landscape',
  },
  {
    id: 2,
    label: 'EMS Slim',
    before: 'https://readdy.ai/api/search-image?query=before%20body%20contouring%20treatment%20side%20profile%20woman%20with%20soft%20midsection%20natural%20body%20beauty%20clinic%20neutral%20background%20clinical%20lighting&width=600&height=450&seq=ba003&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=after%20EMS%20slim%20body%20contouring%20toned%20flat%20stomach%20defined%20waist%20lean%20physique%20neutral%20background%20clinical%20beauty%20clinic%20photo&width=600&height=450&seq=ba004&orientation=landscape',
  },
  {
    id: 3,
    label: 'Kalıcı Kaş',
    before: 'https://readdy.ai/api/search-image?query=before%20microblading%20eyebrow%20sparse%20thin%20eyebrows%20uneven%20shape%20natural%20face%20neutral%20beauty%20photo%20clinical%20lighting&width=600&height=450&seq=ba005&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=after%20PhiBrows%20microblading%20beautiful%20natural%20full%20defined%20eyebrows%20perfect%20arch%20symmetrical%20result%20beauty%20close%20up%20clinical%20lighting&width=600&height=450&seq=ba006&orientation=landscape',
  },
];

function BeforeAfterSlider({ before, after }: { before: string; after: string }) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setPosition(pct);
  }, []);

  const onMouseDown = () => { isDragging.current = true; };
  const onMouseUp = () => { isDragging.current = false; };
  const onMouseMove = (e: React.MouseEvent) => { if (isDragging.current) updatePosition(e.clientX); };
  const onTouchMove = (e: React.TouchEvent) => { updatePosition(e.touches[0].clientX); };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-64 md:h-96 rounded-2xl overflow-hidden cursor-ew-resize select-none"
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onMouseMove={onMouseMove}
      onTouchMove={onTouchMove}
    >
      {/* After image (full) */}
      <img src={after} alt="Sonra" className="absolute inset-0 w-full h-full object-cover object-top" />
      {/* After label */}
      <div className="absolute top-3 right-3 bg-gradient-to-r from-[#D4AF37] to-[#FFB3C6] text-white text-xs font-bold px-3 py-1.5 rounded-full z-10">
        SONRA
      </div>

      {/* Before image (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${position}%` }}>
        <img src={before} alt="Önce" className="absolute inset-0 w-full h-full object-cover object-top" style={{ width: `${10000 / position}%`, maxWidth: 'none' }} />
      </div>
      {/* Before label */}
      <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full z-10">
        ÖNCE
      </div>

      {/* Divider */}
      <div className="absolute top-0 bottom-0 w-0.5 bg-white z-20" style={{ left: `${position}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-lg z-30">
          <i className="ri-drag-move-line text-[#D4AF37] text-sm"></i>
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [active, setActive] = useState(0);

  return (
    <section id="sonuclar" className="py-20 bg-[#FFF8F0]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.3em] mb-3 block">Gerçek Sonuçlar</span>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4">
            Önce &amp; Sonra
            <br />
            <em className="text-[#D4AF37]">Farkı Görün</em>
          </h2>
          <p className="text-[#6B6B6B] text-sm max-w-md mx-auto mb-2">
            Kaydırarak gerçek dönüşümleri keşfedin
          </p>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mt-4" />
        </div>

        <BeforeAfterSlider before={slides[active].before} after={slides[active].after} />

        {/* Thumbnails */}
        <div className="flex justify-center gap-3 mt-6">
          {slides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActive(i)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                active === i
                  ? 'bg-[#D4AF37] text-white'
                  : 'bg-white text-[#6B6B6B] border border-[#FFE5EC] hover:border-[#D4AF37]'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href="https://wa.me/905412190629?text=Merhaba%2C%20tedavi%20sonuçları%20hakkında%20bilgi%20almak%20istiyorum."
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#1DA851] transition-all hover:-translate-y-0.5 whitespace-nowrap"
          >
            <i className="ri-whatsapp-line"></i>
            Siz de Bu Sonuçlara Ulaşın
          </a>
        </div>
      </div>
    </section>
  );
}
